import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FaChevronLeft, FaChevronRight, FaDownload, FaUser, FaCalendarAlt, FaCalendar } from "react-icons/fa";
import '../styles/EmployeeAttendance.css';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';

function EmployeeAttendance() {
     const token = sessionStorage.getItem("token");
     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const [employees, setEmployees] = useState([]);
     const [selectedEmployee, setSelectedEmployee] = useState("");
     const [attendance, setAttendance] = useState({ days: [] });
     const [selectedMonth, setSelectedMonth] = useState("");
     const [selectedToMonth, setSelectedToMonth] = useState("");
     const [employeeInputValue, setEmployeeInputValue] = useState("");
     const [currentPage, setCurrentPage] = useState(1);
     const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
     const [employeeType, setEmployeeType] = useState(""); // NEW: Track employee type
     const [attendanceStrategy, setAttendanceStrategy] = useState(null); // NEW: Strategy instance
     const selectedEmployeeObj = employees.find(emp => emp.employee_code === selectedEmployee);
     const selectedEmployeeName = selectedEmployeeObj ? selectedEmployeeObj.employee_name : "";
     const rowsPerPage = 30;
     const days = Array.isArray(attendance.days) ? attendance.days : [];
     const totalPages = Math.ceil(days.length / rowsPerPage);
     const paginatedDays = days.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

     useEffect(() => {
          const handleResize = () => {
               setIsMobile(window.innerWidth <= 768);
          };
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     const fetchEmployees = async () => {
          try {
               const response = await fetch(`${BASE_URL}/getEmployees`, {
                    method: "GET",
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`,
                    },
               });
               const result = await response.json();
               setEmployees(Array.isArray(result.data) ? result.data : []);
          } catch (error) {
               console.error("Error fetching employees:", error);
          }
     };

     const fetchAttendance = async () => {
          try {
               // Validate inputs
               if (!selectedEmployee) {
                    console.error('❌ No employee selected');
                    return;
               }
               if (!selectedMonth || !selectedToMonth) {
                    console.error('❌ No date range selected');
                    return;
               }

               const url = `${BASE_URL}/getEmployeeAttendance`;
               const payload = {
                    employee_code: selectedEmployee,
                    selectedMonth: selectedMonth,
                    selectedToMonth: selectedToMonth
               };

               console.log('📤 Fetching attendance:', payload);

               const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload)
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const json = await response.json();
               console.log('📥 Full API Response:', json);

               if (json.status && json.data) {
                    // Ensure days array exists
                    const attendanceData = {
                         ...json.data,
                         days: Array.isArray(json.data.days) ? json.data.days : []
                    };

                    console.log('✅ Setting attendance with', attendanceData.days.length, 'days');
                    setAttendance(attendanceData);

                    // Get employee type and strategy with fallback
                    const empType = json.data.employee_info?.emp_type || 'Contractual_Employee';
                    console.log('👤 Employee Type:', empType);
                    setEmployeeType(empType);

                    try {
                         const strategy = AttendanceStrategyFactory.getStrategy(empType);
                         console.log('📋 Strategy loaded:', strategy?.constructor?.name);
                         setAttendanceStrategy(strategy);
                    } catch (strategyError) {
                         console.error('❌ Error loading strategy:', strategyError);
                         // Use default strategy as fallback
                         const defaultStrategy = AttendanceStrategyFactory.getStrategy('Contractual_Employee');
                         setAttendanceStrategy(defaultStrategy);
                    }

                    if (attendanceData.days.length === 0) {
                         console.warn('⚠️ API returned 0 attendance days');
                    }
               } else {
                    console.error('❌ API returned:', { status: json.status, hasData: !!json.data });
                    setAttendance({ days: [] });
               }
          } catch (err) {
               console.error('❌ Error fetching attendance:', err);
               setAttendance({ days: [] });

               // Set default strategy on error
               try {
                    const defaultStrategy = AttendanceStrategyFactory.getStrategy('Contractual_Employee');
                    setAttendanceStrategy(defaultStrategy);
               } catch (fallbackError) {
                    console.error('❌ Even fallback strategy failed:', fallbackError);
               }
          }
     };

     useEffect(() => {
          fetchEmployees();
     }, []);

     useEffect(() => {
          if (selectedMonth && selectedToMonth && selectedEmployee) {
               fetchAttendance();
               setCurrentPage(1);
          }
     }, [selectedMonth, selectedToMonth, selectedEmployee]);

     // NEW: Use strategy to calculate attendance

     const calculateAttendanceStatus = (day, roster, holidays) => {
          // Check if strategy exists first
          if (!attendanceStrategy) {
               return { status: "-", lateMark: "-", workingHours: "-", statusColor: "#94a3b8", remarks: "" };
          }

          // Prepare employee data from attendance state
          const empData = {
               weekAttendance: attendance.weekAttendance || [],
               allDays: attendance.days || [],
               region: attendance.employee_region || 'Other'
          };

          // Get status from strategy
          const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, empData);

          // Get today's date in YYYY-MM-DD format using local time zone
          const now = new Date();
          const todayString = [
               now.getFullYear(),
               String(now.getMonth() + 1).padStart(2, '0'),
               String(now.getDate()).padStart(2, '0')
          ].join('-');

          // Normalize both dates to ensure clean comparison
          const dayDateNormalized = String(day.date).trim();
          const todayNormalized = todayString.trim();

          const isToday = dayDateNormalized === todayNormalized;
          const hasPunchIn = Boolean(day.punch_in);
          const hasPunchOut = Boolean(day.punch_out);

          // Get current time to check if day is still ongoing
          const currentHour = now.getHours();
          const isDayStillOngoing = currentHour < 23; // Before 11 PM

          // Override LOP to OnDuty for current day if:
          // 1. It's today
          // 2. Employee has punched in
          // 3. Status is LOP
          // 4. Day is still ongoing (optional - before 11 PM)
          if (isToday && hasPunchIn && result.status === "LOP" && isDayStillOngoing) {
               console.log('✅ OVERRIDING TO ONDUTY for date:', dayDateNormalized);
               return {
                    status: "OnDuty",
                    lateMark: result.lateMark || "-",
                    workingHours: hasPunchOut ? result.workingHours : "-",
                    statusColor: "#3b82f6", // Blue
                    remarks: "Currently on duty"
               };
          }

          return result;
     };

     const calculateShortfallSummary = () => {
          let totalShortfallSeconds = 0;
          let totalWorkedSeconds = 0;
          let totalPresentDays = 0;
          let totalWeekendDays = 0;
          let exemptionLimit = "00:00:00"; // Default exemption limit

          days.forEach(day => {
               const dateObj = new Date(day.date);
               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);

               // Get exemption limit from first roster entry (should be same for all)
               if (roster && roster.exemption_limit && exemptionLimit === "00:00:00") {
                    const exemptionHours = parseFloat(roster.exemption_limit);
                    const hours = Math.floor(exemptionHours);
                    const minutes = Math.round((exemptionHours - hours) * 60);
                    exemptionLimit = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
               }

               // Count weekend days
               const isSunday = dateObj.getDay() === 0;
               const isWeekOff = status === "Week Off" || status === "Weekoff";
               if (isSunday || isWeekOff) {
                    totalWeekendDays++;
               }

               // Count shortfall in seconds
               if (day.has_shortfall) {
                    totalShortfallSeconds += day.shortfall_seconds || 0;
               }

               // Count worked hours and present days
               if (day.punch_in && day.punch_out) {
                    totalPresentDays++;
                    // Use worked_seconds from backend
                    totalWorkedSeconds += day.worked_seconds || 0;
               }
          });

          // Convert seconds to HH:MM:SS format
          const secondsToHMS = (totalSeconds) => {
               const hours = Math.floor(totalSeconds / 3600);
               const minutes = Math.floor((totalSeconds % 3600) / 60);
               const seconds = totalSeconds % 60;
               return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          };

          return {
               shortfallDays: days.filter(day => day.has_shortfall).length,
               totalShortfall: secondsToHMS(totalShortfallSeconds),
               totalWorkedHours: secondsToHMS(totalWorkedSeconds),
               totalPresentDays: totalPresentDays,
               totalWeekendDays: totalWeekendDays,
               exemptionLimit: exemptionLimit
          };
     };
     // Add to your JSX before the table
     const shortfallSummary = calculateShortfallSummary();

     const handleDownloadExcel = () => {
          if (!days.length) return;

          const data = days.map(day => {
               const dateObj = new Date(day.date);
               const dd = String(dateObj.getDate()).padStart(2, "0");
               const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
               const yyyy = dateObj.getFullYear();
               const formattedDate = `${dd}-${mm}-${yyyy}`;

               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status, lateMark, workingHours, remarks } = calculateAttendanceStatus(day, roster, attendance.holidays);

               // Format times for display
               const formatTimeForExcel = (timeString) => {
                    if (!timeString) return "-";
                    if (timeString.includes('T') || timeString.includes(' ')) {
                         return new Date(timeString).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false
                         });
                    }
                    return timeString;
               };

               // Convert seconds to HH:MM:SS format
               const secondsToHMS = (seconds) => {
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    const secs = seconds % 60;
                    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
               };

               return {
                    // Employee Information
                    "Employee Code": selectedEmployee,
                    "Employee Name": selectedEmployeeName,
                    "Employee Type": employeeType,

                    // Date Information
                    "Date": formattedDate,
                    "Day": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()],

                    // Punch Information
                    "Punch In": formatTimeForExcel(day.punch_in),
                    "Punch Out": formatTimeForExcel(day.punch_out),

                    // Working Hours & Shortfall
                    "Working Hours": workingHours || "-",
                    "Worked Seconds": day.worked_seconds || 0,
                    "Worked Hours (HH:MM:SS)": day.worked_seconds ? secondsToHMS(day.worked_seconds) : "-",
                    "Shortfall Seconds": day.shortfall_seconds || 0,
                    "Shortfall Hours (HH:MM:SS)": day.shortfall_hours || "00:00:00",
                    "Has Shortfall": day.has_shortfall ? "Yes" : "No",

                    // Shift Information
                    "Shift Name": roster?.shift_name || "-",
                    "Shift In Time": roster?.in_time || "-",
                    "Shift Out Time": roster?.out_time || "-",
                    "Shift Total Hours": roster?.total_hours || "-",
                    "Shift Total Minutes": roster?.total_minutes || "-",

                    // Grace Period Information
                    "Grace In (Minutes)": roster?.grace_in || "-",
                    "Grace Out (Minutes)": roster?.grace_out || "-",
                    "Exemption Limit (Hours)": roster?.exemption_limit || "-",

                    // Attendance Status
                    "Status": status,
                    "Late Mark": lateMark,

                    // Week Off Information
                    "Week Off Type": attendance.employee_info?.week_off || "-",
                    "Is Custom Week Off": roster?.custom_weekoff_date ? "Yes" : "No",
                    "Custom Week Off Date": roster?.custom_weekoff_date || "-",

                    // Additional Information
                    "Remarks": remarks || ""
               };
          });

          // Create summary data as first sheet
          const summaryData = [{
               "Summary Type": "Employee Information",
               "Employee Code": selectedEmployee,
               "Employee Name": selectedEmployeeName,
               "Employee Type": employeeType,
               "Week Off Type": attendance.employee_info?.week_off || "-",
               "": "",
               "Month Range": `${attendance.month_range?.start_month_name} ${attendance.month_range?.start_year}`,
               "Total Days": attendance.attendance?.total_days || 0,
               "Present Days": attendance.attendance?.present_days || 0,
               "Absent Days": attendance.attendance?.absent_days || 0,
               "": " ",
               "Total Working Days": days.filter(d => d.punch_in && d.punch_out).length,
               "Days with Shortfall": days.filter(d => d.has_shortfall).length,
               "Total Worked Hours": shortfallSummary.totalWorkedHours,
               "Total Shortfall Hours": shortfallSummary.totalShortfall,
               "Weekend Days": shortfallSummary.totalWeekendDays,
               "Exemption Limit": shortfallSummary.exemptionLimit
          }];

          // Create workbook with multiple sheets
          const wb = XLSX.utils.book_new();

          // Add summary sheet
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

          // Add detailed attendance sheet
          const attendanceWs = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(wb, attendanceWs, "Detailed Attendance");

          // Generate filename with date range
          const filename = `${selectedEmployeeName}_${employeeType}_Attendance_${attendance.month_range?.start}_to_${attendance.month_range?.end}.xlsx`;

          XLSX.writeFile(wb, filename);
     };



     const renderDesktopTable = () => (
          <div className="table-responsive">
               <table className="attendance-table">
                    <thead>
                         <tr>
                              <th>DATE</th>
                              <th>DAY</th>
                              <th>IN</th>
                              <th>OUT</th>
                              <th>SHIFT</th>
                              <th>TOTAL HRS</th>
                              <th>LATE MARK</th>
                              <th>STATUS</th>
                         </tr>
                    </thead>
                    <tbody>
                         {paginatedDays.map(day => {
                              const dateObj = new Date(day.date);
                              const dd = String(dateObj.getDate()).padStart(2, "0");
                              const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                              const yyyy = dateObj.getFullYear();
                              const formattedDate = `${dd}-${mm}-${yyyy}`;

                              // Find roster for the day
                              const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);

                              // Use correct roster fields
                              const shiftName = roster?.shift_name || "-";
                              const shiftIn = roster?.in_time || "-";
                              const shiftOut = roster?.out_time || "-";

                              // Calculate attendance status
                              const { status, lateMark, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

                              // Weekoff and Sunday highlight
                              const isWeekoff = status === "Week Off" || status === "Weekoff";
                              const isSunday = dateObj.getDay() === 0;

                              // Show working hours only if both punches exist
                              const showWorkingHours = day.punch_in && day.punch_out ? workingHours : "-";

                              return (
                                   <tr
                                        key={day.date}
                                        className={isWeekoff || isSunday ? "weekoff-row" : ""}
                                        style={(isWeekoff || isSunday) ? { backgroundColor: '#eff6ff' } : {}}
                                   >
                                        <td>{formattedDate}</td>
                                        <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                        <td>{day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
                                        <td>{day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
                                        <td>
                                             {shiftName !== "-" ? (
                                                  <>
                                                       {shiftName}
                                                       <br />
                                                       <span style={{ fontSize: "11px", color: "#64748b" }}>
                                                            {shiftIn} - {shiftOut}
                                                       </span>
                                                  </>
                                             ) : "-"}
                                        </td>
                                        <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{showWorkingHours}</td>
                                        <td className={lateMark === "Late" ? "late-mark" : ""}>{lateMark}</td>
                                        <td>
                                             <span className="status-badge" style={{ backgroundColor: statusColor }}>
                                                  {status}
                                             </span>
                                        </td>
                                   </tr>
                              );
                         })}
                    </tbody>
               </table>
          </div>
     );

     const renderMobileCards = () => {
          return (
               <div className="mobile-cards-container">
                    {paginatedDays.map((day, index) => {
                         const dateObj = new Date(day.date);
                         const dd = String(dateObj.getDate()).padStart(2, "0");
                         const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                         const yyyy = dateObj.getFullYear();
                         const formattedDate = `${dd}-${mm}-${yyyy}`;

                         const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                         const shiftName = roster?.ShiftName || "-";
                         const { status, lateMark, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

                         const isWeekoff = status === "Week Off";
                         const isSunday = dateObj.getDay() === 0;

                         return (
                              <div
                                   key={day.date || index}
                                   className="mobile-card-item"
                                   style={{
                                        background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff',
                                        border: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                                   }}
                              >
                                   {/* Header */}
                                   <div className="mobile-card-header" style={{
                                        background: (isWeekoff || isSunday) ? '#dbeafe' : '#f8fafc',
                                        borderBottom: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                                   }}>
                                        <div className="mobile-card-date-info">
                                             <span className="mobile-card-date" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {formattedDate}
                                             </span>
                                             <span className="mobile-card-day" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e40af' : '#64748b',
                                                  background: (isWeekoff || isSunday) ? '#bfdbfe' : '#f1f5f9'
                                             }}>
                                                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}
                                             </span>
                                        </div>
                                        <span className="mobile-card-status" style={{ background: statusColor }}>
                                             {status}
                                        </span>
                                   </div>

                                   {/* Body */}
                                   <div className="mobile-card-body" style={{
                                        background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff'
                                   }}>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">IN</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">OUT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">SHIFT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {shiftName}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">TOTAL HRS</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b',
                                                  fontFamily: 'monospace',
                                                  fontWeight: '700'
                                             }}>
                                                  {workingHours}
                                             </span>
                                        </div>
                                        {lateMark && lateMark !== "-" && (
                                             <div className="mobile-card-row">
                                                  <span className="mobile-card-label">LATE MARK</span>
                                                  <span className="mobile-card-value" style={{
                                                       color: '#ef4444',
                                                       fontWeight: '700'
                                                  }}>
                                                       {lateMark}
                                                  </span>
                                             </div>
                                        )}
                                   </div>
                              </div>
                         );
                    })}
               </div>
          );
     };

     const formatDate = (dateString) => {
          const dateObj = new Date(dateString);
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const yyyy = dateObj.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
     };

     const formatTime = (timeString) => {
          if (!timeString) return '-';

          // If it's already a full datetime string
          if (timeString.includes('T') || timeString.includes(' ')) {
               return new Date(timeString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          }

          // If it's just a time string like "09:00:00"
          if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
               return timeString; // Return full "09:00:00" including seconds
          }

          return timeString;
     };

     // Fix the Doctor render functions
     const renderDoctorMobileCards = () => {
          return (
               <div className="mobile-cards-container">
                    {paginatedDays.map((day, index) => {
                         const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                         const { status, workingHours } = calculateAttendanceStatus(day, roster, attendance.holidays);

                         return (
                              <div key={index} className="mobile-attendance-card">
                                   <div className="card-header">
                                        <span className="date">{formatDate(day.date)}</span>
                                        <span className={`status ${status?.toLowerCase()}`}>
                                             {status || (day.punch_in ? "Present" : "Absent")}
                                        </span>
                                   </div>
                                   <div className="card-body">
                                        <div className="time-row">
                                             <span>Punch In: {formatTime(day.punch_in)}</span>
                                        </div>
                                        <div className="time-row">
                                             <span>Punch Out: {formatTime(day.punch_out)}</span>
                                        </div>
                                        <div className="working-hours">
                                             Working Hours: {workingHours || '-'}
                                        </div>
                                   </div>
                              </div>
                         );
                    })}
               </div>
          );
     };




     const renderDoctorDesktopTable = () => {
          return (
               <div className="table-responsive">
                    <table className="attendance-table">
                         <thead>
                              <tr>
                                   <th>DATE</th>
                                   <th>DAY</th>
                                   <th>IN</th>
                                   <th>OUT</th>
                                   <th>HRS Worked</th>
                                   <th>Shift Hours</th>
                                   <th>Shortfall</th>
                                   <th>SHIFT</th>
                                   <th>STATUS</th>
                              </tr>
                         </thead>
                         <tbody>
                              {paginatedDays.map((day, index) => {
                                   const dateObj = new Date(day.date);
                                   const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                                   const { status, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

                                   // Calculate row styling
                                   const isPresent = day.punch_in && day.punch_out;
                                   const isWeekOff = status === "Week Off" || status === "Weekoff";
                                   const hasShortfall = isPresent && day.has_shortfall;

                                   // Determine row class
                                   let rowClass = '';
                                   if (isWeekOff) {
                                        rowClass = 'weekoff-row';
                                   } else if (hasShortfall) {
                                        rowClass = 'shortfall-row';
                                   }

                                   // Calculate display values
                                   const shiftHours = (isPresent && roster?.total_hours) ? roster.total_hours : '-';
                                   const shortfallDisplay = isPresent ? (day.shortfall_hours || (hasShortfall ? '00:00' : 'Complete')) : '-';
                                   const shiftDisplay = roster ? `${roster.shift_name || '-'} | ${formatTime(roster.in_time)} - ${formatTime(roster.out_time)}` : '-';

                                   return (
                                        <tr key={index} className={rowClass}>
                                             <td>{formatDate(day.date)}</td>
                                             <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                             <td>{formatTime(day.punch_in)}</td>
                                             <td>{formatTime(day.punch_out)}</td>
                                             <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{workingHours}</td>
                                             <td style={{ fontFamily: 'monospace' }}>{shiftHours}</td>
                                             <td style={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: '600',
                                                  color: hasShortfall ? '#dc3545' : (isPresent ? '#28a745' : '#6b7280')
                                             }}>
                                                  {shortfallDisplay}
                                             </td>
                                             <td>{shiftDisplay}</td>
                                             <td>
                                                  <span className="status-badge" style={{ backgroundColor: statusColor }}>
                                                       {status || (day.punch_in ? "Present" : "Absent")}
                                                  </span>
                                             </td>
                                        </tr>
                                   );
                              })}
                         </tbody>
                    </table>
               </div>
          );
     };


     const renderAttendanceView = () => {
          if (employeeType === "DOCTOR") {
               return isMobile ? renderDoctorMobileCards() : renderDoctorDesktopTable();
          } else {
               // Default rendering for Contractual_Employee and others
               return isMobile ? renderMobileCards() : renderDesktopTable();
          }
     };
     return (
          <div className="employee-attendance-wrapper">
               <div className="attendance-header">
                    <h2>Employee Attendance</h2>
                    {employeeType && (
                         <small style={{ marginTop: "5px" }}>
                              -  {employeeType}
                         </small>
                    )}
               </div>

               <div className="attendance-main-card">
                    <div className="filters-container row">
                         <div className="col-12 col-md-3 filter-group employee-filter">
                              <label>
                                   <FaUser /> Employee
                              </label>
                              <input
                                   className="filter-input"
                                   type="text"
                                   placeholder="Select Employee"
                                   list="employees"
                                   value={employeeInputValue}
                                   onChange={e => {
                                        setEmployeeInputValue(e.target.value);
                                        const found = employees.find(emp =>
                                             `${emp.employee_code} - ${emp.employee_name}` === e.target.value
                                        );
                                        setSelectedEmployee(found ? found.employee_code : "");
                                        setAttendance({ days: [] });
                                        setSelectedMonth("");
                                        setSelectedToMonth("");
                                   }}
                                   autoComplete="off"
                              />
                              <datalist id="employees">
                                   {employees.map(emp => (
                                        <option
                                             key={emp.emp_id}
                                             value={`${emp.employee_code} - ${emp.employee_name}`}
                                        />
                                   ))}
                              </datalist>
                         </div>

                         <div className="col-12 col-md-2 filter-group month-filter">
                              <label>
                                   <FaCalendarAlt /> From
                              </label>
                              <input
                                   type="month"
                                   className="filter-input"
                                   min="2025-08"
                                   value={selectedMonth}
                                   onChange={e => {
                                        const newMonth = e.target.value;
                                        setSelectedMonth(newMonth);
                                        setSelectedToMonth(newMonth); // ✅ Automatically set To month same as From month
                                   }}
                              />
                         </div>

                         <div className="col-12 col-md-2 filter-group month-filter">
                              <label>
                                   <FaCalendarAlt /> To
                              </label>
                              <input
                                   type="month"
                                   className="filter-input"
                                   min={selectedMonth}
                                   value={selectedToMonth}
                                   onChange={e => setSelectedToMonth(e.target.value)}
                                   disabled={!selectedMonth}
                              />
                         </div>

                         <div className="col-12 col-md-2 filter-group action-buttons">
                              <button
                                   className="download-btn"
                                   onClick={handleDownloadExcel}
                                   disabled={!days.length}
                              >
                                   <FaDownload /> {!isMobile && "Download Excel"}
                              </button>
                         </div>

                         <div className="col-12 col-md-2 filter-group action-buttons">
                              <Link
                                   to="/employee-attendance/calendar"
                                   state={{
                                        selectedEmployee: selectedEmployee,
                                        selectedEmployeeName: selectedEmployeeName,
                                        selectedMonth: selectedMonth,
                                        employeeType: employeeType
                                   }}
                              >
                                   <button
                                        className="calendar-view-btn"
                                        disabled={!selectedEmployee || !selectedMonth}
                                   >
                                        <FaCalendar /> {!isMobile && "Calendar View"}
                                   </button>
                              </Link>
                         </div>
                    </div>

                    {days.length > 0 ? (
                         <>



                              {/* Summary Cards - Move above table and only show for Doctor */}
                              {employeeType === "DOCTOR" && (
                                   <div className="summary-cards" style={{ marginTop: '20px' }}>
                                        <div className="summary-card">
                                             <h4>Total Present Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalPresentDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Worked Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalWorkedHours}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Weekend Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalWeekendDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Days with Shortfall</h4>
                                             <span className="summary-number">{shortfallSummary.shortfallDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Shortfall Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalShortfall}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Exemption Limit</h4>
                                             <span className="summary-number">{shortfallSummary.exemptionLimit}</span>
                                        </div>
                                   </div>
                              )}

                              {employeeType === "CONTRACTUAL_EMPLOYEE" && (
                                   <div className="summary-cards" style={{ marginTop: '20px' }}>
                                        <div className="summary-card">
                                             <h4>Total Present Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalPresentDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Worked Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalWorkedHours}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Weekend Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalWeekendDays}</span>
                                        </div>

                                   </div>
                              )}

                              {renderAttendanceView()}
                              <div className="pagination-container">
                                   <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                   >
                                        <FaChevronLeft />
                                   </button>
                                   <span className="pagination-info">
                                        Page {currentPage} of {totalPages || 1}
                                   </span>
                                   <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                   >
                                        <FaChevronRight />
                                   </button>
                                   <span className="total-records">
                                        Total: {days.length}
                                   </span>
                              </div>
                         </>
                    ) : (
                         selectedEmployee && selectedMonth && selectedToMonth ? (
                              <div className="no-data-message">
                                   <p>No attendance records found</p>
                              </div>
                         ) : (
                              <div className="no-data-message">
                                   <p>Please select employee and date range to view attendance</p>
                              </div>
                         )
                    )}
               </div>
          </div>
     );
}

export default EmployeeAttendance;